"use client";
import type { GameData } from "@/app/games/(details)/types";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { cn } from "@/lib/utils";
import { GalleryVerticalIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { CarouselIndicator } from "../Carouselndicator";
import { ArtGalleryModal } from "./ArtGalleryModal";

interface ArtGalleryCarouselProps {
  className?: string;
  gameData: GameData;
}

export const ArtGalleryCarousel = ({
  gameData,
  className,
}: ArtGalleryCarouselProps) => {
  const { track } = useGamePageAnalytics();
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) =>
        prev === (gameData.gameContent?.art_gallery_thumbnail_urls?.length ?? 0)
          ? 1
          : prev + 1,
      );
    }, 3000);
    return () => clearInterval(interval);
  }, [gameData.gameContent?.art_gallery_thumbnail_urls?.length]);

  const handleOpenModal = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "art-gallery-carousel",
        section: GamePageSection.BENTO_BOXES,
        controlType: ControlType.BUTTON,
      },
    });

    setIsModalOpen(true);
  }, [track]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") handleOpenModal();
    },
    [handleOpenModal],
  );

  return (
    <>
      <ArtGalleryModal
        gameData={gameData}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />

      <div
        className={cn(
          "relative glass-border glass-border-hover cursor-pointer rounded-[1.25rem]",
          className,
        )}
        onClick={handleOpenModal}
        onKeyDown={handleKeyDown}
        // biome-ignore lint/a11y/useSemanticElements: use div instead of button as we have child elements not supported by <button>
        role="button"
        tabIndex={0}
      >
        {/* Grid layout to stack images on top of each other */}
        <div className="absolute inset-0 grid grid-cols-1 grid-rows-1">
          {gameData.gameContent?.art_gallery_thumbnail_urls?.map(
            (url, index) => (
              <Image
                key={url}
                src={url}
                priority={index === currentIndex - 1}
                alt={`Art Gallery ${index + 1}`}
                fill
                quality={50}
                loading={index === currentIndex - 1 ? "eager" : "lazy"}
                sizes="(max-width: 768px) 33vw, (max-width: 1024px) 25vw, 45vw"
                className={cn(
                  "col-start-1 row-start-1 object-cover rounded-[1.25rem] transition-all duration-200",
                  index === currentIndex - 1 ? "opacity-100" : "opacity-0",
                )}
              />
            ),
          )}
        </div>

        <div className="absolute inset-0 size-full rounded-[1.25rem] pointer-events-none bg-darken-overlay" />

        <div className="absolute left-[10%] top-[10%] text-neutral-50 font-suisse flex items-center gap-x-1.5 @4xl:flex-col @4xl:items-start">
          <GalleryVerticalIcon className="@md:size-6 size-5" />
          <p className="@md:text-lg text-sm font-semibold">Art Gallery</p>
        </div>

        <CarouselIndicator
          currentIndex={currentIndex}
          total={gameData.gameContent?.art_gallery_thumbnail_urls?.length ?? 0}
          className="absolute bottom-5 right-5"
        />
      </div>
    </>
  );
};
